import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";

const Carousel = ({ project }) => {
  const [current, setCurrent] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isLoading, setIsLoading] = useState({});
  
  const slides = useMemo(() => project.slides || [project.imageUrl], [project.slides, project.imageUrl]);
  const total = slides.length;
  const intervalRef = useRef(null);
  const containerRef = useRef(null);

  const startAutoplay = useCallback(() => {
    if (total <= 1 || !isPlaying) return;
    intervalRef.current = setInterval(() => {
      setCurrent((p) => (p + 1) % total);
    }, 4000);
  }, [total, isPlaying]);

  const stopAutoplay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return stopAutoplay;
  }, [startAutoplay, stopAutoplay]);

  const goToSlide = useCallback((index) => {
    setCurrent(index);
    stopAutoplay();
    if (isPlaying) {
      setTimeout(startAutoplay, 100);
    }
  }, [isPlaying, startAutoplay, stopAutoplay]);

  const nextSlide = useCallback(() => {
    goToSlide((current + 1) % total);
  }, [current, total, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((current - 1 + total) % total);
  }, [current, total, goToSlide]);

  const togglePlayPause = useCallback(() => {
    setIsPlaying(prev => {
      if (prev) {
        stopAutoplay();
      } else {
        startAutoplay();
      }
      return !prev;
    });
  }, [startAutoplay, stopAutoplay]);

  const handleImageLoad = useCallback((index) => {
    setIsLoading(prev => ({ ...prev, [index]: false }));
  }, []);

  const handleImageLoadStart = useCallback((index) => {
    setIsLoading(prev => ({ ...prev, [index]: true }));
  }, []);

  const handleTouchStart = useCallback((e) => {
    if (total <= 1) return;
    setIsDragging(true);
    setStartX(e.touches[0].clientX);
    stopAutoplay();
  }, [total, stopAutoplay]);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging || total <= 1) return;
    const currentX = e.touches[0].clientX;
    const diff = startX - currentX;
    setTranslateX(-diff);
  }, [isDragging, startX, total]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging || total <= 1) return;
    setIsDragging(false);
    
    const threshold = 50;
    if (Math.abs(translateX) > threshold) {
      if (translateX > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    
    setTranslateX(0);
    if (isPlaying) {
      setTimeout(startAutoplay, 100);
    }
  }, [isDragging, translateX, total, nextSlide, prevSlide, isPlaying, startAutoplay]);

  const handleMouseDown = useCallback((e) => {
    if (total <= 1) return;
    setIsDragging(true);
    setStartX(e.clientX);
    stopAutoplay();
  }, [total, stopAutoplay]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging || total <= 1) return;
    const diff = startX - e.clientX;
    setTranslateX(-diff);
  }, [isDragging, startX, total]);

  const handleMouseUp = useCallback(() => {
    if (!isDragging || total <= 1) return;
    setIsDragging(false);
    
    const threshold = 50;
    if (Math.abs(translateX) > threshold) {
      if (translateX > 0) {
        nextSlide();
      } else {
        prevSlide();
      }
    }
    
    setTranslateX(0);
    if (isPlaying) {
      setTimeout(startAutoplay, 100);
    }
  }, [isDragging, translateX, total, nextSlide, prevSlide, isPlaying, startAutoplay]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  useEffect(() => {
    slides.forEach((_, index) => {
      handleImageLoadStart(index);
    });
  }, [slides, handleImageLoadStart]);

  if (total <= 1) {
    return (
      <div className="carousel single-slide">
        <div className="slide-wrapper">
          <img 
            src={slides[0]} 
            alt={project.title} 
            className="slide-image"
            loading="lazy"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="carousel" ref={containerRef}>
      <div 
        className="slides-container"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onMouseDown={handleMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div 
          className="slides-track"
          style={{
            transform: `translateX(${(-current * 100) + (translateX / (containerRef.current?.offsetWidth || 1) * 100)}%)`,
            transition: isDragging ? 'none' : 'transform 0.6s cubic-bezier(0.4, 0.15, 0.1, 0.95)'
          }}
        >
          {slides.map((slide, i) => (
            <div key={i} className="slide-item">
              {isLoading[i] && <div className="slide-loading">Loading...</div>}
              <img
                src={slide}
                alt={`${project.title} ${i + 1}`}
                className="slide-image"
                onLoad={() => handleImageLoad(i)}
                onLoadStart={() => handleImageLoadStart(i)}
                loading="lazy"
                draggable={false}
              />
            </div>
          ))}
        </div>

        <button
          className="carousel-arrow prev"
          onClick={prevSlide}
          aria-label="Previous slide"
        >
          ‹
        </button>
        
        <button
          className="carousel-arrow next"
          onClick={nextSlide}
          aria-label="Next slide"
        >
          ›
        </button>

        {total > 1 && (
          <button
            className="carousel-playpause"
            onClick={togglePlayPause}
            aria-label={isPlaying ? "Pause slideshow" : "Play slideshow"}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>
        )}

        <div className="slide-progress">
          <div 
            className="slide-progress-bar"
            style={{ 
              width: `${((current + 1) / total) * 100}%`
            }}
          />
        </div>
      </div>

      <div className="slide-indicators">
        {slides.map((_, i) => (
          <button
            key={i}
            className={`indicator ${i === current ? "active" : ""}`}
            onClick={() => goToSlide(i)}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
